import type { ComponentProps, ReactNode } from 'react'
import moment from 'moment'
import Group from 'components/utils/Group'
import AccommodationPackageCancellationSection from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackageCancellationSection'
import type { FlashCancellationPolicyView } from './getFlashCancellationPolicyView'
import OfferPropertyCancellationPolicies from './OfferPropertyCancellationPolicies'
import OfferPropertyFlexibleCancellationPoliciesView from './OfferPropertyFlexibleCancellationPoliciesView'
import OfferPropertyNonRefundablePolicy from './OfferPropertyNonRefundablePolicy'

export interface FlashCancellationPoliciesViewProps {
  /** Compact package-selection presentation, including its policy divider. */
  compact?: boolean
  policyView: FlashCancellationPolicyView
  propertyTimezoneOffset?: number
  isFlashUSP?: boolean
  hideNonRefundable?: boolean
  /**
   * Optional leading affordance (e.g. shield). Only rendered when at least one policy
   * branch is visible — avoids orphan icons while charter dates resolve or windows expire.
   */
  leadingIcon?: ReactNode
  /** Horizontal gap between the leading icon and the policy copy. Defaults to the medium text-block gap. */
  leadingIconGap?: ComponentProps<typeof Group>['gap']
  showHotelOnly?: boolean
  enableNonAmendableWording?: boolean
  textVariant?: 'small' | 'medium'
}

/**
 * Presentational Flash cancellation gates. No Redux — callers pass a resolved policyView.
 */
function FlashCancellationPoliciesView(props: FlashCancellationPoliciesViewProps) {
  const {
    policyView,
    compact,
    propertyTimezoneOffset,
    isFlashUSP,
    hideNonRefundable,
    leadingIcon,
    leadingIconGap = 8,
    showHotelOnly = false,
    enableNonAmendableWording = false,
    textVariant,
  } = props

  // Live wall-clock check (same predicate as OfferPropertyCancellationPolicies) so memoized
  // policyView eligibility cannot leave a leadingIcon up after the free-cancel window expires.
  const showFreeCancellation = policyView.showFreeCancellation
    && !policyView.freeCancellationStartDate?.isBefore(moment())
  const showFlexibleCancellation = !!policyView.flexibleTimelineContext
  const showNonRefundable = !hideNonRefundable && policyView.showNonRefundable
  const hasVisiblePolicy = showFreeCancellation || showFlexibleCancellation || showNonRefundable

  if (!hasVisiblePolicy) {
    return null
  }

  const content = (
    <>
      {showFreeCancellation && <OfferPropertyCancellationPolicies
        compact={compact}
        propertyTimezoneOffset={propertyTimezoneOffset}
        checkInDate={policyView.policyReferenceDate}
        isFlashUSP={isFlashUSP}
        showHotelOnly={showHotelOnly}
      />}
      {showFlexibleCancellation && policyView.flexibleTimelineContext && <OfferPropertyFlexibleCancellationPoliciesView
        compact={compact}
        details={policyView.flexibleTimelineContext.details}
        isCharter={policyView.flexibleTimelineContext.isCharter}
        isFlashUSP={isFlashUSP}
        showHotelOnly={showHotelOnly}
        textVariant={textVariant}
      />}
      {showNonRefundable && <OfferPropertyNonRefundablePolicy compact={compact} enableNonAmendableWording={enableNonAmendableWording && !policyView.isCharter} />}
    </>
  )

  const policyContent = <>
    {!!leadingIcon && <Group direction="horizontal" gap={leadingIconGap} verticalAlign="start">
      {leadingIcon}
      {content}
    </Group>}
    {!leadingIcon && content}
  </>

  return <>
    {compact && <AccommodationPackageCancellationSection>{policyContent}</AccommodationPackageCancellationSection>}
    {!compact && policyContent}
  </>
}

export default FlashCancellationPoliciesView
